/**
 * Finding an item by typing part of its name or code (owner rule O50 / D251: nothing in the
 * warehouse depends on a barcode scanner; every item is picked from a list with search). People
 * type Arabic in different ways, so both the query and the names are folded the same way first:
 *
 * - أ إ آ ٱ → ا · ة → ه · ى → ي · ؤ → و · ئ → ي
 * - harakat, the dagger alif and the tatweel (ـ) are dropped
 * - Arabic-Indic digits → Latin digits; Latin letters → lower case
 * - spaces, punctuation and invisible marks are dropped ("بيبسي علبة" finds "بيبسي-علبة")
 */
import { latinDigits, stripInvisible } from '../network/address.ts';

const LETTER_FOLDS: ReadonlyArray<readonly [RegExp, string]> = [
  [/[أإآٱ]/g, 'ا'],
  [/ة/g, 'ه'],
  [/ى/g, 'ي'],
  [/ؤ/g, 'و'],
  [/ئ/g, 'ي'],
];

/** Harakat (U+064B–U+0652), dagger alif (U+0670), tatweel (U+0640). */
const MARKS = /[ً-ْٰـ]/g;

/** Everything that is not a letter or a digit (in any script). */
const SEPARATORS = /[^\p{L}\p{N}]/gu;

export function foldForSearch(text: string): string {
  let folded = latinDigits(stripInvisible(text)).toLowerCase().replace(MARKS, '');
  for (const [pattern, replacement] of LETTER_FOLDS) folded = folded.replace(pattern, replacement);
  return folded.replace(SEPARATORS, '');
}

/**
 * True when every word of the query appears in one of the texts (folded). An empty query matches
 * everything. "بيبسي علب" matches "بيبسي علبة 330"; "pep" matches "Pepsi can".
 */
export function matchesSearch(
  query: string,
  texts: readonly (string | null | undefined)[],
): boolean {
  const words = query
    .split(/\s+/)
    .map(foldForSearch)
    .filter((word) => word !== '');
  if (words.length === 0) return true;
  const haystack = texts.filter((t): t is string => typeof t === 'string').map(foldForSearch);
  return words.every((word) => haystack.some((text) => text.includes(word)));
}
